import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Sidebar } from "@/components/layout/sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RenderJob } from "@/lib/user-types";
import { Skeleton } from "@/components/ui/skeleton";
import { RenderStatusBadge } from "@/components/render-status-badge";
import { getStyleById } from "@/lib/render-styles";
import { 
  Clock, 
  Download, 
  Share2, 
  Flag, 
  AlertTriangle,
  Film,
  ArrowLeft,
  Archive,
  RefreshCw
} from "lucide-react";
import { 
  Progress 
} from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { format, formatDistanceToNow } from "date-fns";
import { useState, useEffect, useCallback } from "react";
import { S3_URL_PREFIX } from "@/lib/environment";
import { RenderJobStatus } from "../../../shared/schema";
import VideoEditor from "../components/video-editor";

export default function RenderPage() {
  const { id } = useParams();
  const [, navigate] = useLocation();
  const [progress, setProgress] = useState(0);
  const [timeLeft, setTimeLeft] = useState<string | null>(null);
  const [s3UrlPrefix, setS3UrlPrefix] = useState('');
  const [mediaCompletion, setMediaCompletion] = useState<{
    total: number;
    remaining: number;
    typeBreakdown: {
      audio: { total: number; remaining: number };
      image: { total: number; remaining: number };
    };
  } | null>(null);
  const [existingImages, setExistingImages] = useState<Set<string>>(new Set());
  
  const renderId = id || "";
  
  // Fetch the S3 URL prefix
  useEffect(() => {
    async function fetchS3UrlPrefix() {
      try {
        const prefix = await S3_URL_PREFIX.get();
        setS3UrlPrefix(prefix);
      } catch (error) {
        console.error('Failed to fetch S3 URL prefix:', error);
      }
    }
    
    fetchS3UrlPrefix();
  }, []);
  
  // Track whether we need to refetch based on render status
  const [shouldRefetch, setShouldRefetch] = useState(false);
  
  // Fetch render job details
  const { 
    data: renderJob, 
    isLoading,
    refetch
  } = useQuery<RenderJob>({
    queryKey: [`/api/renders/${renderId}`],
    enabled: !!renderId,
    refetchInterval: shouldRefetch ? 10000 : false,
  });

  // Set existing images for editing modal
  useEffect(() => {
      if (renderJob?.screenplaySnapshot?.blocks) {
        const urls = renderJob.screenplaySnapshot.blocks.flatMap(b => b.imageUrl ?? []);
        setExistingImages(new Set(urls));
      }
    }, [renderJob]);

  const onGenerateImage = async (blockId: string, userPrompt: string | null = null): Promise<string | null> => {
    const block = renderJob?.screenplaySnapshot?.blocks?.find(b => b.id === blockId);
    if (!block){
      console.log(`Block ${blockId} not found`);
      return null;
    }

    const response = await fetch("/api/renders/blocks/image", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        blockId,
        renderJobId: renderId, 
        imagePrompt: userPrompt || block.imagePrompt // allow the user to create a custom prompt
      })
    });

    if (!response.ok) {
      const { message } = await response.json();
      console.log(`Failed to generate image: ${message}`);
    }

    const { url } = await response.json();

    // Add to local block state
    if(block.imageUrl)
    {
      block.imageUrl.push(url);
    } else {
      block.imageUrl = [url];
    }

    existingImages.add(url); // keep the picker set in sync

    return url;
  };

  const { toast } = useToast();
  
  const handleRegenerateVideo = async () => {
    if (!renderJob) return;
    
    try {
      const response = await apiRequest("POST", "/api/renders/re-render", {
        renderJobId: renderJob.id
      });
      
      if (response.ok) {
        toast({
          title: "Re-render Started",
          description: "The re-render is in process. You'll be notified when it's complete.",
        });
      } else {
        const errorData = await response.json();
        toast({
          title: "Re-render Failed",
          description: errorData.message || "Failed to start re-render",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Re-render Failed",
        description: "An error occurred while starting the re-render",
        variant: "destructive",
      });
    }
  }
  
  // Fetch media completion data for non-complete renders
  const { 
    data: mediaCompletionData,
    refetch: refetchMediaCompletion
  } = useQuery<{
    total: number;
    remaining: number;
    typeBreakdown: {
      audio: { total: number; remaining: number };
      image: { total: number; remaining: number };
    };
  }>({
    queryKey: [`/api/renders/mediaCompletion/${renderId}`],
    enabled: !!renderId && renderJob?.status !== RenderJobStatus.COMPLETE,
    refetchInterval: shouldRefetch ? 10000 : false,
    staleTime: 5000, // Consider data fresh for 5 seconds
  });
  
  // Update refetch status based on render job status
  useEffect(() => {
    if (renderJob) {
      setShouldRefetch(renderJob.status === RenderJobStatus.RENDERING 
        || renderJob.status === RenderJobStatus.COMPILING 
        || renderJob.status === RenderJobStatus.QUEUED);
    }
  }, [renderJob]);
  
  // Calculate remaining time for rendering jobs
  const calculateTimeLeft = useCallback(() => {
    if (!renderJob || renderJob.status !== RenderJobStatus.RENDERING || !renderJob.estimatedCompletionTime) {
      return null;
    }
    
    const completionTime = new Date(renderJob.estimatedCompletionTime);
    const now = new Date();
    
    if (completionTime <= now) {
      return 'almost done';
    }
    
    return formatDistanceToNow(completionTime, { addSuffix: false });
  }, [renderJob]);

  // Calculate progress based on media completion data
  const calculateProgress = useCallback(() => {
    if (!renderJob) return 0;
    
    if (renderJob.status === RenderJobStatus.COMPLETE) return 100;
    if (renderJob.status === RenderJobStatus.FAILED) return 0;
    if (renderJob.status === RenderJobStatus.QUEUED) return 5;
    
    // Use media completion data if available
    if (mediaCompletionData && (renderJob.status === RenderJobStatus.RENDERING || renderJob.status === RenderJobStatus.COMPILING)) {
      const total = mediaCompletionData.total || 0;
      const remaining = mediaCompletionData.remaining || 0;
      
      if (total > 0) {
        const completedMedia = total - remaining;
        const mediaProgress = (completedMedia / total) * 90; // 90% for media generation
        
        // If all media is complete but render job isn't complete, we're compiling
        if (remaining === 0) {
          return 90; // Leave 10% for video compilation
        }
        
        return Math.max(5, Math.round(mediaProgress)); // Minimum 5% for started jobs
      }
    }
    
    // Fallback to time-based progress for rendering jobs
    if (renderJob.status === RenderJobStatus.RENDERING) {
      const createdAt = new Date(renderJob.createdAt);
      const completionTime = renderJob.estimatedCompletionTime 
        ? new Date(renderJob.estimatedCompletionTime) 
        : new Date(createdAt.getTime() + 3 * 60 * 60 * 1000); // Default 3 hours
      
      const totalDuration = completionTime.getTime() - createdAt.getTime();
      const elapsedDuration = Date.now() - createdAt.getTime();
      
      return Math.min(Math.round((elapsedDuration / totalDuration) * 89), 89); // Max 89% for time-based
    }
    
    return 5;
  }, [renderJob, mediaCompletionData]);

  // Update progress bar
  useEffect(() => {
    const newProgress = calculateProgress();
    setProgress(newProgress);
    
    // Set up interval for time updates only
    if (renderJob && renderJob.status === RenderJobStatus.RENDERING) {
      const interval = setInterval(() => {
        setTimeLeft(calculateTimeLeft());
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [renderJob, mediaCompletionData, calculateProgress, calculateTimeLeft]);
  
  // Initial time left calculation
  useEffect(() => {
    setTimeLeft(calculateTimeLeft());
  }, [calculateTimeLeft]);
  
  const handleBackToScreenplay = () => {
    if (renderJob) {
      navigate(`/screenplays/${renderJob.screenplayId}`);
    } else {
      navigate('/dashboard');
    }
  };

  // Get render style details
  const styleInfo = renderJob ? getStyleById(renderJob.style) : undefined;
  
  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 ml-0 md:ml-64">
        <main className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="flex items-center mb-4">
              <Button variant="ghost" onClick={handleBackToScreenplay} className="mr-2">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              
              {!isLoading && renderJob && (
                <div className="ml-auto flex items-center gap-2">
                  {renderJob.status !== RenderJobStatus.COMPLETE && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        refetch();
                        refetchMediaCompletion();
                      }}
                      disabled={isLoading}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  )}
                  <RenderStatusBadge status={renderJob.status} className="text-xs px-3 py-1" />
                </div>
              )}
            </div>
            
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-4 w-48" />
              </div>
            ) : renderJob ? (
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">{renderJob.title}</h1>
                <p className="text-sm text-gray-500">
                  {styleInfo?.name || renderJob.style} • Created on {format(new Date(renderJob.createdAt), "MMMM d, yyyy")}
                </p>
              </div>
            ) : (
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Render not found</h1>
              </div>
            )}
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="py-4 space-y-6">
              {isLoading ? (
                <RenderDetailsSkeleton />
              ) : renderJob ? (
                <>
                  {/* Render Status */}
                  {renderJob.status === RenderJobStatus.RENDERING 
                  || renderJob.status === RenderJobStatus.COMPILING
                  || renderJob.status === RenderJobStatus.QUEUED ? (
                    <Card>
                      <CardHeader>
                        <CardTitle>Render Progress</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium flex items-center">
                              <Clock className="h-4 w-4 mr-2 text-primary" />
                              {renderJob.status === RenderJobStatus.RENDERING ? 'Rendering in progress' 
                              : renderJob.status === RenderJobStatus.COMPILING ? 'Compiling video in progress'
                              : 'Queued for rendering'}
                            </span>
                            <span>{timeLeft ? `Estimated time left: ${timeLeft}` : ''}</span>
                          </div>
                          
                          <Progress value={progress} className="h-2" />
                          
                          {/* Media Completion Details */}
                          {mediaCompletionData && (renderJob.status === RenderJobStatus.RENDERING || renderJob.status === RenderJobStatus.COMPILING) && (
                            <div className="text-sm text-gray-600 space-y-2">
                              <div className="flex justify-between items-center">
                                <span>Media Files Progress:</span>
                                <span className="font-medium">
                                  {mediaCompletionData.total - mediaCompletionData.remaining} / {mediaCompletionData.total} completed
                                </span>
                              </div>
                              
                              {mediaCompletionData.typeBreakdown && (
                                <div className="grid grid-cols-2 gap-4 text-xs">
                                  <div className="flex justify-between">
                                    <span>Audio:</span>
                                    <span>
                                      {mediaCompletionData.typeBreakdown.audio.total - mediaCompletionData.typeBreakdown.audio.remaining} / {mediaCompletionData.typeBreakdown.audio.total}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Images:</span>
                                    <span>
                                      {mediaCompletionData.typeBreakdown.image.total - mediaCompletionData.typeBreakdown.image.remaining} / {mediaCompletionData.typeBreakdown.image.total}
                                    </span>
                                  </div>
                                </div>
                              )}
                              
                              {mediaCompletionData.remaining === 0 && (
                                <p className="text-blue-600 font-medium">
                                  All media files completed. Compiling final video...
                                </p>
                              )}
                            </div>
                          )}
                          
                          <p className="text-sm text-gray-500">
                            {renderJob.status === RenderJobStatus.RENDERING
                              ? "Your screenplay is being processed. This can take a few hours depending on the length." 
                              : "Your render is in the queue and will start processing soon."}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}
                  
                  {/* Movie Poster and Downloads - Side by side for completed renders */}
                  {renderJob.status === RenderJobStatus.COMPLETE ? (
                    <div className="grid lg:grid-cols-2 gap-6">
                      {/* Movie Poster */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Movie Poster</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {renderJob.posterImagePath ? (
                            <div className="overflow-hidden rounded-lg">
                              <img 
                                src={`${renderJob.posterImagePath}`} 
                                alt={`${renderJob.title} poster`} 
                                className="w-full h-auto max-h-[300px] object-contain mx-auto"
                              />
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center py-12 bg-gray-100 rounded-lg">
                              <Film className="h-12 w-12 text-gray-400 mb-4" />
                              <p className="text-gray-500">
                                No poster image available for this render.
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Video & Files Downloads */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Video & Files</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-6">
                            {renderJob.outputFilePath && (
                              <div>
                                <h3 className="text-lg font-medium mb-2">Final Video</h3>
                                <Button asChild>
                                  <a href={`${renderJob.outputFilePath}`} download>
                                    <Download className="h-4 w-4 mr-2" />
                                    Download Video
                                  </a>
                                </Button>
                              </div>
                            )}

                            {renderJob.combinedAudioPath && (
                              <div>
                                <h3 className="text-lg font-medium mb-2">Final Audio</h3>
                                <Button asChild>
                                  <a href={`${renderJob.combinedAudioPath}`} download>
                                    <Download className="h-4 w-4 mr-2" />
                                    Download Audio Only
                                  </a>
                                </Button>
                              </div>
                            )}
                            
                            {renderJob.projectZipPath && (
                              <div>
                                <h3 className="text-lg font-medium mb-2">Project Files</h3>
                                <Button variant="outline" asChild>
                                  <a href={`${s3UrlPrefix}${renderJob.projectZipPath}`} download>
                                    <Archive className="h-4 w-4 mr-2" />
                                    Download ZIP Project File
                                  </a>
                                </Button>
                              </div>
                            )}
                            
                            {renderJob.shareableLink && (
                              <div>
                                <h3 className="text-lg font-medium mb-2">Share</h3>
                                <div className="flex items-center">
                                  <Input 
                                    value={renderJob.shareableLink} 
                                    readOnly 
                                    className="mr-2"
                                  />
                                  <Button variant="outline" onClick={() => {
                                    navigator.clipboard.writeText(renderJob.shareableLink || "");
                                  }}>
                                    <Share2 className="h-4 w-4 mr-2" />
                                    Copy
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ) : (
                    /* Single column layout for non-complete renders */
                    <Card>
                      <CardHeader>
                        <CardTitle>Movie Poster</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {renderJob.status === RenderJobStatus.FAILED ? (
                          <div className="flex flex-col items-center justify-center py-12 text-center">
                            <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Render Failed</h3>
                            <p className="text-gray-500 max-w-md">
                              There was an issue rendering your screenplay. Please try again or contact support.
                            </p>
                            <Button className="mt-4" onClick={() => refetch()}>
                              Retry
                            </Button>
                          </div>
                        ) : renderJob.posterImagePath !== null ? 
                        <CardContent>
                          {renderJob.posterImagePath ? (
                            <div className="overflow-hidden rounded-lg">
                              <img 
                                src={`${renderJob.posterImagePath}`} 
                                alt={`${renderJob.title} poster`} 
                                className="w-full h-auto max-h-[300px] object-contain mx-auto"
                              />
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center py-12 bg-gray-100 rounded-lg">
                              <Film className="h-12 w-12 text-gray-400 mb-4" />
                              <p className="text-gray-500">
                                No poster image available for this render.
                              </p>
                            </div>
                          )}
                        </CardContent>                        
                        : (
                          <div className="flex flex-col items-center justify-center py-12 bg-gray-100 rounded-lg">
                            <Film className="h-12 w-12 text-gray-400 mb-4" />
                            <p className="text-gray-500">
                              {renderJob.status === RenderJobStatus.RENDERING
                                ? "Your movie poster will appear here once rendering is complete." 
                                : "The movie poster will be generated when your render starts processing."}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* VideoEditor Section */}
                  {renderJob && renderJob.status === RenderJobStatus.COMPLETE &&(
                    <VideoEditor
                      renderJobId={renderJob.id}
                      parentBlocks={renderJob.screenplaySnapshot.blocks}
                      renderJobPages={renderJob.screenplaySnapshot.pageCount}
                      currentVideoUrl={renderJob.outputFilePath}
                      onGenerateImage={onGenerateImage}
                      onRegenerateVideo={handleRegenerateVideo}
                      existingImages={existingImages}
                    />
                  )}
                  
                  {/* Character Voices Section */}
                  {renderJob && renderJob.characterVoices && Object.keys(renderJob.characterVoices).length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Character Voices</CardTitle>
                        <CardDescription>
                          Voice assignments used for this rendering
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="grid grid-cols-12 font-medium text-sm text-muted-foreground mb-2">
                            <div className="col-span-4">Character</div>
                            <div className="col-span-8">Voice</div>
                          </div>
                          
                          <div className="divide-y">
                            {Object.entries(renderJob.characterVoices).map(([character, voice]) => (
                              <div key={character} className="grid grid-cols-12 py-2 items-center">
                                <div className="col-span-4 font-medium">{character}</div>
                                <div className="col-span-8">
                                  {typeof voice === 'string' 
                                    ? voice // For old format (string only)
                                    : (
                                      <span className="flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-primary">
                                          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                                          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                                          <line x1="12" y1="19" x2="12" y2="23"/>
                                          <line x1="8" y1="23" x2="16" y2="23"/>
                                        </svg>
                                        {voice.name} 
                                      </span>
                                    )
                                  }
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center py-8">
                      <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Render Not Found</h3>
                      <p className="text-gray-500 mb-4">
                        The render you're looking for doesn't exist or you don't have permission to view it.
                      </p>
                      <Button onClick={() => navigate('/dashboard')}>
                        Back to Dashboard
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

// Input component is already imported at the top of the file

function RenderDetailsSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-5 w-48" />
            </div>
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 bg-gray-100 rounded-lg">
            <Skeleton className="h-12 w-12 rounded-full mb-4" />
            <Skeleton className="h-5 w-64" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
